import { css, html, nothing } from "lit";
import { getPodletMetaData, setupCustomElement } from "../../lit-util.js";
import WarpElement from "@warp-ds/elements-core";
import { PodletMetaData } from "../../../types/types-drEdition.js";
import { logCreativeClick, logCreativeLoad, logCreativeView } from "../../tracking/sponsored-content-tracking.js";
import { RecommerceExpandableModuleEntry, RecommerceExpandableModuleFields } from "../../../types/types-contentful.js";
import { renderRichText } from "../utils/contentful-rich-text-render-switch.js";
import { CurrentPlatform, getCurrentPlatform } from "../utils/getCurrentPlatform.js";
import { detectIOSEnvironment } from "../utils/index.js";
import "@warp-ds/elements/button";
import "@warp-ds/icons/elements/link-external-16";
import "@warp-ds/icons/elements/chevron-up-16";
import "@warp-ds/icons/elements/chevron-down-16";
import { i18n as customI18n } from "../../i18n.js";

const NAME = "contentful-expandable-module-wrapper";

export class ContentfulExpandableModuleWrapper extends WarpElement {
	declare creativeId: string;
	declare entryId: string;
	declare metaData: PodletMetaData;
	declare contentModel: RecommerceExpandableModuleEntry;
	declare currentPlatform: CurrentPlatform;
	declare adPosition: string;
	declare isExpanded: boolean;

	constructor() {
		super();
		this.creativeId = "";
		this.entryId = "";
		this.metaData = getPodletMetaData();
		this.contentModel = {} as RecommerceExpandableModuleEntry;
		this.currentPlatform = {} as CurrentPlatform;
		this.isExpanded = false;

		getCurrentPlatform(this, (currentPlatform) => {
			this.currentPlatform = currentPlatform;
			this.requestUpdate(); // Make sure to update after font-size change

			const setFontSize = () => {
				const html = document.querySelector("html");
				if (html) {
					const iOSEnvironment = detectIOSEnvironment();

					if (iOSEnvironment.isWebView) {
						html.style.fontSize = "175%";
					} else {
						html.style.fontSize = "62.5%";
					}
				}

				this.requestUpdate();
			};

			if (document.readyState === "loading") {
				document.addEventListener("DOMContentLoaded", setFontSize);
			} else {
				setFontSize();
			}
		});
	}

	static properties = {
		id: { type: String },
		entryId: { type: String },
		creativeId: { type: String },
		contentModel: { type: Object },
		isMobile: { type: Boolean },
		isExpanded: { type: Boolean, state: true },
	};

	static styles = [
		...super.styles,
		css`
			@unocss-placeholder;
		`,
		css`
			:host > article {
				padding: 0;
				color: #333;
				min-height: 10rem;
				overflow: hidden;
				transition:
					background-color 0.3s ease,
					color 0.3s ease;
			}

			button {
				transition: border-color 0.3s ease;
			}

			:host > article.dark-mode * {
				color: white;
			}
			.button-icon {
				transform: translateY(2px);
			}

			.area-logo {
				grid-area: logo;
			}

			.area-content {
				grid-area: content;
			}

			.area-image {
				grid-area: image;
			}

			.container {
				min-width: 350px;
				container-type: inline-size;
			}

			.header-grid {
				gap: 1.6rem;
				grid-template-columns: 1fr 12rem;
				grid-template-rows: auto 1fr 5rem;
				grid-template-areas:
					"content logo"
					"content image"
					"content .";
			}

			@container (min-width: 600px) {
				.header-grid {
					gap: 2.4rem;
					grid-template-columns: 15rem 1fr 12rem;
					grid-template-rows: auto;
					grid-template-areas:
						"image content logo"
						"image content .";
				}
			}

			.section {
				display: grid;
				gap: 16px;
				grid-template-areas:
					"image"
					"content";
			}

			@container (min-width: 600px) {
				.section {
					gap: 24px;
					grid-template-columns: 2fr 1fr;
					grid-template-areas: "content image";
				}
			}

			/* Animated expand/collapse using CSS Grid */
			.expandable {
				display: grid;
				grid-template-rows: 0fr;
				transition: grid-template-rows 0.4s ease-out;
			}

			.expanded.expandable {
				grid-template-rows: 1fr;
			}

			.expandable-content {
				overflow: hidden;
			}

			/** The line below is where the injected CSS goes, removing it means you get no CSS from the design system **/
			*,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.gap-10{gap:1rem}.static{position:static}.h-64{height:6.4rem}.mt-32{margin-top:3.2rem}.pt-4{padding-top:.4rem}.transition{transition-property:color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,-webkit-backdrop-filter,backdrop-filter;transition-duration:.15s;transition-timing-function:cubic-bezier(.4,0,.2,1)}.ease-out{transition-timing-function:cubic-bezier(0,0,.2,1)}.text-xl{font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)};
		`,
		...WarpElement.styles, // Adds the global styles
	] as unknown as never;

	getAdPositionFromParentComponent() {
		// Get this dynamically instead
		return this.currentPlatform.isMobile ? "board_2" : "netboard_2";
	}

	firstUpdated() {
		if (!this.metaData.isWebView) {
			this.adPosition = this.getAdPositionFromParentComponent();
			this.initialTracking();
		}
	}

	initialTracking() {
		this.trackLoad();
		this.setupTrackView();
	}

	setupTrackView() {
		const target = this.shadowRoot?.getElementById(this.creativeId);
		if (target) {
			let viewTimeout: NodeJS.Timeout | null = null; // Timer for tracking view

			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							// Start timer when the element is in view
							viewTimeout = setTimeout(() => {
								this.trackView();
								observer.disconnect();
							}, 1000);
						} else if (viewTimeout) {
							// Clear the timer if the element is out of view
							clearTimeout(viewTimeout);
							viewTimeout = null;
						}
					});
				},
				{
					threshold: 0.5,
				},
			);
			observer.observe(target);
		}
	}

	trackLoad() {
		logCreativeLoad({
			adCreative: this.creativeId,
			adPosition: this.adPosition,
			verticalName: this.metaData?.vertical || "",
			platform: this.currentPlatform.platform,
			provider: this.contentModel.fields.advertiserName,
		});
	}

	trackClick() {
		const fields = this.contentModel.fields as RecommerceExpandableModuleFields;
		logCreativeClick({
			adCreative: this.creativeId,
			adPosition: this.adPosition,
			verticalName: this.metaData?.vertical || "",
			platform: this.currentPlatform.platform,
			targetId: fields.url || "",
			provider: fields.advertiserName || "",
		});
	}

	trackView() {
		logCreativeView({
			adCreative: this.creativeId,
			adPosition: this.adPosition,
			verticalName: this.metaData?.vertical || "",
			platform: this.currentPlatform.platform,
			provider: this.contentModel.fields.advertiserName,
		});
	}

	getImageBasedOnDevice() {
		const { mobileMainImage, mainImage } = this.contentModel.fields;
		if (this.currentPlatform.isMobile && mobileMainImage && mobileMainImage.fields) {
			return {
				data: mobileMainImage.fields,
				fieldId: "mobileMainImage",
			};
		}
		return {
			data: mainImage.fields,
			fieldId: "mainImage",
		};
	}

	toggleExpandable(e) {
		e?.preventDefault?.();
		this.isExpanded = !this.isExpanded;
		// if you want to keep tracking:
		//this.trackClick?.(e);
	}

	render() {
		const { contentModel, metaData } = this;

		const fields = contentModel?.fields as RecommerceExpandableModuleFields;
		console.log("Rendering with fields:", fields);
		if (!fields) return html``;

		const {
			url,
			title,
			tagline,
			advertiserLogo,
			ctaButtonText,
			expandableButtonText,
			expandableCloseButtonText,
			expandableContentSections,
		} = fields;
		const imageField = this.getImageBasedOnDevice();

		const textColor = metaData.darkMode ? "s-text-inverted" : "s-text";

		return html`
		<aside
				id="sponsor-header"
				class="sponsor-container flex items-center justify-between p-8 h-64 gap-10 
				${this.metaData.darkMode ? "dark-mode" : ""}"
			>
				<p class="font-bold text-m m-0 ${this.metaData.darkMode ? "s-text-inverted" : "s-text"}">
					${customI18n.t({
						id: "common.sponsorLabel",
						message: "Annonsørinnhold",
					})}
				</p>
			</aside>
			<article
				class="container relative ${metaData.darkMode ? "dark-mode s-bg-inverted" : `s-bg ${metaData.isWebView ? "" : ""}`}"
				id="${this.creativeId}"
			>
				<header class="grid header-grid">
					<contentful-image
						class="area-logo"
						width="auto"
						fieldId="logoImageData"
						entryId="${this.entryId}"
						.imageData="${advertiserLogo.fields}"
						imageStyle=""
					></contentful-image>
					<div class="area-content">
						<h2 .fieldId="title" .entryId="${this.entryId}" class="font-normal text-xl pt-4 ${textColor}">${title}</h2>
						${
							tagline
								? html`<contentful-rich-text
										data-contentful-entry-id="${this.entryId}"
										data-contentful-field-id="tagline"
										>${renderRichText(tagline, `pt-4 ${textColor}`)}</contentful-rich-text
									>`
								: nothing
						}
					</div>
					<contentful-image
						.imageData="${imageField.data}"
						class="area-image"
						imageStyle="width: 100%;border-radius: 8px;"
						entryId="${this.entryId}"
						fieldId="${imageField.fieldId}"
					></contentful-image>
				</header>

				<div id="expandable-content" class="expandable ${this.isExpanded ? "expanded" : ""}" aria-hidden="${!this.isExpanded}">
					<div class="expandable-content">
						${
							this.isExpanded && expandableContentSections.length > 0
								? expandableContentSections.map(
										(section) => html`
											<section class="section mt-32">
												<div style="grid-area: content;">
													<h3>${section.fields.title}</h3>
													<p>${section.fields.text}</p>
												</div>
												<contentful-image
													.imageData="${section.fields.image.fields}"
													imageStyle="width: 100%;grid-area: image;border-radius: 8px;"
													entryId="${this.entryId}"
													fieldId="${imageField.fieldId}"
												></contentful-image>
											</section>
										`,
									)
								: nothing
						}

						<footer class="mt-32">
							<w-button variant="primary" href="${url}">
								<span class="flex gap-8"> ${ctaButtonText} <w-icon-link-external-16 aria-hidden="true" /> </span>
							</w-button>
						</footer>
					</div>
				</div>

				<div class="absolute bottom-0 right-0" style="width: 12rem;">
					<w-button
						full-width
						@click=${this.toggleExpandable}
						aria-expanded="${this.isExpanded}"
						aria-controls="expandable-content"
					>
						<span class="flex items-center justify-center gap-8">
							${this.isExpanded ? expandableCloseButtonText : expandableButtonText}
							<span class="button-icon" aria-hidden="true">
								${
									this.isExpanded
										? html`<w-icon-chevron-up-16></w-icon-chevron-up-16>`
										: html`<w-icon-chevron-down-16></w-icon-chevron-down-16>`
								}
							</span>
					</w-button>
				</div>
			</article>
		`;
	}
}

setupCustomElement(NAME, ContentfulExpandableModuleWrapper);
